import { CommonModule } from '@angular/common';
import { Component, inject, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { FilterMatchMode, FilterMetadata, FilterService, MenuItem, MessageService, SelectItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { ImageModule } from 'primeng/image';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { PaginatorModule } from 'primeng/paginator';
import { SelectModule } from 'primeng/select';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';

import { firstValueFrom, forkJoin } from 'rxjs';
import { SETTINGS, Settings } from '../../../../app.settings';
import { ProcessingComponent } from '../../../../components';

import {
  IAppUser,
  IOrderItemInstanceAddVendorDelayInfoRequest,
  IOrderItemInstanceAddVendorDeliveryDateRequest,
  IOrderItemInstanceAddVendorNameAndSitePermitRequest,
  IPermits,
  IVendor,
  InsectaryDashboardService,
  ISearchRequest,
  ISearchResult,
  ISearchSort,
  ISearchRequestFilter,
  ISearchFilterValue,
  VendorAdminService
} from '../../../../services/insecta';

import { Breadcrumb } from 'primeng/breadcrumb';
import { FloatLabel } from 'primeng/floatlabel';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { notifyAndTrackError } from '../../../../common/error';
import { dateToIsoString, getItemDescriptionColumn, isNotEmpty, isStringEmpty, toShortDate } from '../../../../common/functions';
import { successMsg, warningMsg } from '../../../../common/messages';
import { AppInitService, IInsectaPermissions } from '../../../../services/app-init.service';
import { AppInsightsService } from '../../../../services/app-insights.service';
import { PermitsAdminService } from '../../../../services/insecta/admin/permitslist-admin.service';
import { OrderItemInstanceService } from '../../../../services/insecta/order-item-instance.service';

@Component({
  selector: 'app-insectary-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    CheckboxModule,
    FormsModule,
    ImageModule,
    InputTextModule,
    SelectModule,
    TableModule,
    ToggleSwitchModule,
    RouterModule,
    ProcessingComponent,
    PaginatorModule,
    InputNumberModule,
    DialogModule,
    DatePickerModule,
    Breadcrumb,
    FloatLabel,
    TextareaModule,
    TooltipModule
  ],
  templateUrl: './vendor-order.component.html'
})
export class VendorOrderComponent implements OnInit {
  @Input() appUser!: IAppUser;
  @ViewChild('dt') dt!: Table;

  userSettingsJson: { [key: string]: any } = {};
  route: any;
  permissions!: IInsectaPermissions;

  filtersActive: boolean = false;
  activeColumnFilters: Set<string> = new Set<string>();
  cols: any[] = [];

  customMatchModeOptions: SelectItem[] = [
    { label: 'Starts With', value: FilterMatchMode.STARTS_WITH },
    { label: 'Contains', value: FilterMatchMode.CONTAINS },
    { label: 'Not Contains', value: FilterMatchMode.NOT_CONTAINS },
    { label: 'Ends With', value: FilterMatchMode.ENDS_WITH },
    { label: 'Equals', value: FilterMatchMode.EQUALS },
    { label: 'Not Equals', value: FilterMatchMode.NOT_EQUALS },
    { label: 'Is Empty', value: 'isEmpty' },
    { label: 'Has Value', value: 'isNotEmpty' }
  ];

  dateMatchModeOptions: SelectItem[] = [
  { label: 'Date Is', value: FilterMatchMode.DATE_IS },
  { label: 'Date Is Not', value: FilterMatchMode.DATE_IS_NOT },
  { label: 'Date Before', value: FilterMatchMode.DATE_BEFORE },
  { label: 'Date After', value: FilterMatchMode.DATE_AFTER },
  { label: 'Is Empty', value: 'isEmpty' },
  { label: 'Has Value', value: 'isNotEmpty' }
];

numericMatchModeOptions: SelectItem[] = [
  { label: 'Equals', value: FilterMatchMode.EQUALS },
  { label: 'Not Equals', value: FilterMatchMode.NOT_EQUALS },
  { label: 'Less Than', value: FilterMatchMode.LESS_THAN },
  { label: 'Less Than or Equal', value: FilterMatchMode.LESS_THAN_OR_EQUAL_TO },
  { label: 'Greater Than', value: FilterMatchMode.GREATER_THAN },
  { label: 'Greater Than or Equal', value: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO },
  { label: 'Is Empty', value: 'isEmpty' },
  { label: 'Has Value', value: 'isNotEmpty' }
];

  showAddVendorNameAndPermitModel: boolean = false;
  showAddVendorDeliveryDateModel: boolean = false;
  showAddVendorDelayInfoModel: boolean = false;
  vendorId: string | null = null;
  vendorSitePermit: string | null = null;
  vendorDelayFlag: boolean = false;
  vendorDelayNote: string = '';

  showExportModel: boolean = false;
  vendorDeliveryDate: Date | null = null;

  showPrintModal = false;

  instances: any[] = [];
  selectAllToggle: boolean = false;
  selectedInstances: any[] = [];

  permits: IPermits[] = [];
  vendors: IVendor[] = [];

  pageVars: any = {
    currentUser: ``,
    isEditing: false,
    isReady: false,
    pageId: `Dashboard:InsectaryDashboard:VendorOrder`,
    breadcrumbItems: <MenuItem[]>[]
  };

  exportColumns: string[] = ['InstanceSequence', 'RequesterName', 'DeliveryDate', 'Insect', 'InsectQuantity'];
  tableColumns: string[] = [
    'OrderId',
    'InstanceId',
    'InstanceSequence',
    'ItemDescription',
    'ItemDescription_C',
    'ItemDescription_S',
    'ItemDescription_CS',
    'DeliveryDate',
    'Insect',
    'InsectQuantity',
    'VendorName',
    'ShippingVendor',
    'VendorDeliveryDate',
    'VendorDelay',
    'VendorDelayNote',
    'StandardInfestationRate',
    'PermitName',
    'SitePermitsName',
    'Note',
    'RequesterName',
    'ProjectName'
  ];

  skip: number = 0;
  take: number = 10;
  totalCount: number = 0;

  insectaryDashboardService: InsectaryDashboardService = inject(InsectaryDashboardService);
  searchRequest: ISearchRequest = {
    columns: [],
    filters: [],
    sorts: [],
    skip: this.skip,
    take: this.take
  };

  itemDescName: string = '';
  siteCode: string = '';

  constructor(
    @Inject(SETTINGS) private settings: Settings,
    private msgService: MessageService,
    private orderItemInstanceService: OrderItemInstanceService,
    private permitService: PermitsAdminService,
    private vendorAdminService: VendorAdminService,
    private appInit: AppInitService,
    private appInsights: AppInsightsService,
    private filterService: FilterService
  ) {
    this.pageVars.pageId = `${settings.environment}:Dashboard:InsectaryDashboard`;
    appInsights.trackPageView(this.pageVars.pageId, `/dashboard/insectarydashboard/vendororder`);
  }

  ngOnInit(): void {
    this.pageVars.isReady = false;
    this.permissions = this.appInit.permissions;
    this.appUser = this.appInit.appUser;

    this.filterService.register('isEmpty', (value: any): boolean => {
      return value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
    });

    this.filterService.register('isNotEmpty', (value: any): boolean => {
      return value !== undefined && value !== null && (typeof value === 'string' && value.trim() !== '');
    });

    if (this.appUser) {
      if (!isStringEmpty(this.appUser.userSettingsJson) || this.appUser.userSettingsJson === '{}') {
        this.userSettingsJson = JSON.parse(this.appUser.userSettingsJson);
      } else {
        this.userSettingsJson = {};
      }

      this.itemDescName = getItemDescriptionColumn(this.userSettingsJson);
      this.siteCode = this.userSettingsJson['Default Site'] || '';
    }

    this.initializeColumns();

    if (this.permissions.canViewInsectaryDashboard && this.permissions.canViewRequestorDashboard) {
      this.pageVars.breadcrumbItems = [{ label: 'Insectary Dashboard', routerLink: '/insectarydashboard' }, { label: 'Vendor Orders' }];
    } else {
      this.pageVars.breadcrumbItems = [{ label: 'Dashboard', routerLink: '/insectarydashboard' }, { label: 'Vendor Orders' }];
    }

    const request = {
      Vendors: this.vendorAdminService.getVendorsAll(),
      permits: this.permitService.getPermitsAll()
    };

    firstValueFrom(forkJoin(request))
      .then((response) => {
        this.vendors = response.Vendors.filter((x) => x.isActive);
        this.permits = response.permits.filter((x) => x.isActive);
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'VendorOrderComponent', action: 'InitLoad' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
        this.pageVars.isEditing = false;
      });
  }

  handleTableEvents(event: TableLazyLoadEvent) {
    this.pageVars.isReady = false;
    this.activeColumnFilters.clear();

    this.searchRequest.filters = [];

    this.tableColumns.forEach((columnName) => {
      const columnFilters = event.filters![columnName] as FilterMetadata[];
      if (columnFilters && columnFilters.length > 0) {
        const tableFilter = <ISearchRequestFilter>{
          fieldname: columnName,
          logic: columnFilters[0].operator,
          values: []
        };
        columnFilters.forEach((filterValue) => {
          if (filterValue.matchMode === 'isEmpty' || filterValue.matchMode === 'isNotEmpty') {
            tableFilter.values.push(<ISearchFilterValue>{
              operator: filterValue.matchMode.toUpperCase(),
              value: ''
            });
          } else if (filterValue.value !== null && filterValue.value !== undefined && filterValue.value !== '') {
            tableFilter.values.push(<ISearchFilterValue>{
              operator: filterValue.matchMode || `contains`,
              value: filterValue.matchMode?.startsWith(`date`) ? toShortDate(filterValue.value) : `${filterValue.value}`
            });
          }
        });
        if (tableFilter.values.length > 0) {
          this.searchRequest.filters.push(tableFilter);
          this.activeColumnFilters.add(columnName);
        }
      }
    });

    this.searchRequest.filters.push(<ISearchRequestFilter>{
      fieldname: 'Site',
      logic: 'and',
      values: [<ISearchFilterValue>{ operator: 'equals', value: `${this.userSettingsJson['Default Site'] || ''}` }]
    });

    this.searchRequest.sorts = [];
    if (isNotEmpty(event.sortField)) {
      this.searchRequest.sorts.push(<ISearchSort>{ name: event.sortField, direction: event.sortOrder === 1 ? 'ASC' : 'DESC' });
    } else {
      this.searchRequest.sorts.push(<ISearchSort>{ name: 'DeliveryDate', direction: 'ASC' });
    }

    this.searchRequest.skip = event.first;
    this.searchRequest.take = event.rows ?? this.take;
    this.filtersActive = this.activeColumnFilters.size > 0 || isNotEmpty(event.sortField);

    this.executeSearch();
  }

  executeSearch() {
    const request = this.searchRequest;
    request.columns = [...this.tableColumns, this.itemDescName];

    firstValueFrom(this.insectaryDashboardService.getVendorTable(request))
      .then(async (response) => {
        this.totalCount = response?.totalCount || 0;
        this.instances = JSON.parse(response?.results ?? `[]`);
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'VendorOrderComponent', action: 'ExecuteSearch' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
      });
  }

  selectRowClick() {
    this.selectAllToggle = this.selectedInstances.length == this.totalCount;
  }

  toggleSelectAll(checkAll: boolean) {
    if (!checkAll) {
      this.selectedInstances = [];
      this.selectAllToggle = false;
      return;
    }

    this.pageVars.isReady = false;

    const selectAllSearch = this.searchRequest;
    selectAllSearch.columns = ['InstanceId'];
    selectAllSearch.skip = 0;
    selectAllSearch.take = this.totalCount;

    firstValueFrom(this.insectaryDashboardService.getVendorTable(selectAllSearch))
      .then((response: ISearchResult) => {
        const results = JSON.parse(response?.results ?? `[]`);
        this.selectedInstances = results.map((x: any) => x.InstanceId);
        this.selectAllToggle = true;
      })
      .catch((err) => {
        this.selectedInstances = [];
        this.selectAllToggle = false;
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'VendorOrderComponent', action: 'SelectAll' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
      });
  }

  addVendorDeliveryDate() {
    this.vendorDeliveryDate = null;
    this.showAddVendorDeliveryDateModel = true;
  }

  addVendorName() {
    this.vendorId = null;
    this.vendorSitePermit = null;
    this.showAddVendorNameAndPermitModel = true;
  }

  addVendorDelayInfo() {
    this.vendorDelayFlag = false;
    this.vendorDelayNote = '';
    this.showAddVendorDelayInfoModel = true;
  }

  get isVendorSelected(): boolean {
    return this.vendorId !== null && this.vendorId !== undefined && this.vendorId !== '';
  }

  get isPermitSelected(): boolean {
    return this.vendorSitePermit !== null && this.vendorSitePermit !== undefined && this.vendorSitePermit !== '';
  }

  onSaveVendorDelayInfo() {
    if (this.selectedInstances.length > 0) {
      this.pageVars.isReady = false;

      const addVendorDelayInfoRequest = <IOrderItemInstanceAddVendorDelayInfoRequest>{
        instances: [...this.selectedInstances],
        vendorDelay: this.vendorDelayFlag,
        vendorDelayNote: this.vendorDelayNote ? this.vendorDelayNote : null
      };
      firstValueFrom(this.orderItemInstanceService.addVendorDelayInfoOrderItemInstance(addVendorDelayInfoRequest))
        .then(() => {
          this.pageVars.isReady = true;
          successMsg(this.msgService, 'Vendor delay information added successfully');
          this.executeSearch();
        })
        .catch((err) => {
          notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'VendorOrderComponent', action: 'SaveVendorDelayInfo' });
        });
    } else {
      warningMsg(this.msgService, 'No order item instance selected');
    }
    this.showAddVendorDelayInfoModel = false;
  }

  onCancelVendorDelayInfo() {
    this.showAddVendorDelayInfoModel = false;
  }

  onSaveVendorNameAndPermit() {
    if (this.selectedInstances.length > 0) {
      this.pageVars.isReady = false;

      const addVendoreNameAndSitePermitRequest = <IOrderItemInstanceAddVendorNameAndSitePermitRequest>{
        instances: [...this.selectedInstances],
        vendorId: this.vendorId ? this.vendorId : null,
        sitePermitId: this.vendorSitePermit ? this.vendorSitePermit : null
      };

      firstValueFrom(this.orderItemInstanceService.addVendorNameAndSitePermitOrderItemInstance(addVendoreNameAndSitePermitRequest))
        .then(() => {
          this.pageVars.isReady = true;
          successMsg(this.msgService, 'Vendor Name and SitePermit added successfully');
          this.executeSearch();
        })
        .catch((err) => {
          notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'VendorOrderComponent', action: 'SaveVendorNameAndPermit' });
        });
    } else {
      warningMsg(this.msgService, 'No order item instance selected');
    }
    this.showAddVendorNameAndPermitModel = false;
  }

  onCancelVendorNameAndPermit() {
    this.showAddVendorNameAndPermitModel = false;
  }

  onSaveVendorDeliveryDate() {
    if (this.selectedInstances.length > 0) {
      this.pageVars.isReady = false;

      const addVendorDeliveryDateRequest = <IOrderItemInstanceAddVendorDeliveryDateRequest>{
        instances: [...this.selectedInstances],
        vendorDeliveryDate: this.vendorDeliveryDate ? dateToIsoString(this.vendorDeliveryDate) : null
      };

      firstValueFrom(this.orderItemInstanceService.addVendorDeliveryDateOrderItemInstance(addVendorDeliveryDateRequest))
        .then(() => {
          this.pageVars.isReady = true;
          successMsg(this.msgService, 'Vendor Delivery Date added successfully');
          this.executeSearch();
        })
        .catch((err) => {
          notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'VendorOrderComponent', action: 'SaveVendorDeliveryDate' });
        });
    } else {
      warningMsg(this.msgService, 'No order item instance selected');
    }
    this.showAddVendorDeliveryDateModel = false;
  }

  onCancelVendorDeliveryDate() {
    this.showAddVendorDeliveryDateModel = false;
  }

  async exportThisList() {
    this.pageVars.isReady = false;

    try {
      const exportRequest = this.searchRequest;
      exportRequest.columns = [...this.exportColumns];
      exportRequest.skip = 0;
      exportRequest.take = this.totalCount;

      await this.insectaryDashboardService.getVendorExport(exportRequest);
    } catch (err) {
      notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'VendorOrderComponent', action: 'ExportInstances' });
    } finally {
      this.pageVars.isReady = true;
    }
  }

  getColumnHeaderStyle(columnName: string): any {
    return this.activeColumnFilters.has(columnName) ? { 'background-color': '#e0f7fa', 'border-bottom': '2px solid #006064' } : {};
  }

  clearFilters(table: Table) {
    table.sortField = null;
    table.sortOrder = 1;
    table.clear();
    if (table.filters) {
    Object.keys(table.filters).forEach((key) => {
      const col = this.cols?.find((c: any) => c.field === key);
      const filters = table.filters[key] as any[];
      if (Array.isArray(filters)) {
        filters.forEach((f) => {
          if (f.matchMode === 'isEmpty' || f.matchMode === 'isNotEmpty') {
          if (col?.type === 'date') {
            f.matchMode = 'dateIs';
          } else if (col?.type === 'numeric') {
            f.matchMode = 'equals';
          } else {
            f.matchMode = 'contains';
          }
            f.value = null;
          }
        });
      }
    });
  }
    this.activeColumnFilters.clear();
    this.filtersActive = false;

    try {
      const stateKey = 'vendor-order-table';
      const storedState = localStorage.getItem(stateKey);
      if (storedState) {
        const state = JSON.parse(storedState);
        delete state.sortField;
        delete state.sortOrder;
        delete state.filters;
        localStorage.setItem(stateKey, JSON.stringify(state));
      }
    } catch (e) {
      console.error('Error updating local storage in clearFilters', e);
    }

    setTimeout(() => {
      this.searchRequest.filters = [];
      this.searchRequest.filters.push(<ISearchRequestFilter>{
        fieldname: 'Site',
        logic: 'and',
        values: [<ISearchFilterValue>{ operator: 'equals', value: `${this.userSettingsJson['Default Site'] || ''}` }]
      });
      this.searchRequest.sorts = [{ name: 'DeliveryDate', direction: 'ASC' }];
      this.searchRequest.skip = 0;
      this.searchRequest.take = this.take;
      this.executeSearch();
    }, 100);
  }

  resetPage(table: Table) {
    table.clear();
    table.clearState();
    window.location.reload();
  }

  initializeColumns() {
    const descField = this.itemDescName || 'ItemDescription';
    this.cols = [
      { field: 'InstanceSequence', header: 'Order Item Instance ID', type: 'text', template: 'link' },
      { field: 'DeliveryDate', header: 'Delivery Date', type: 'date' },
      { field: descField, header: 'Item Description', type: 'text', template: 'tooltip', style: { 'min-width': '250px' } },
      { field: 'InsectQuantity', header: 'Quantity', type: 'numeric' },
      { field: 'VendorName', header: 'Vendor', type: 'text' },
      { field: 'VendorDeliveryDate', header: 'Vendor Delivery Date', type: 'date' },
      { field: 'VendorDelayNote', header: 'Delay Information', type: 'text', template: 'tooltip', style: { 'min-width': '250px' } },
      { field: 'SitePermitsName', header: 'Shipping Permit', type: 'text', template: 'tooltip', style: { 'min-width': '250px' } },
      { field: 'PermitName', header: 'Requester\'s Permit', type: 'text', template: 'tooltip', style: { 'min-width': '250px' } },
      { field: 'Note', header: 'Note', type: 'text', template: 'tooltip', style: { 'min-width': '250px' } },
      { field: 'RequesterName', header: 'Requester', type: 'text' },
      { field: 'ProjectName', header: 'Project', type: 'text', template: 'tooltip', style: { 'min-width': '250px' } }
    ];

    this.syncColumnState(descField);
  }

  private syncColumnState(variantField: string) {
    const stateKey = 'vendor-order-table';
    try {
      const storedState = localStorage.getItem(stateKey);
      if (!storedState) return;

      const state = JSON.parse(storedState);
      const columnOrder = state?.columnOrder as string[] | undefined;

      if (columnOrder && !columnOrder.includes(variantField)) {
        delete state.columnOrder;
        localStorage.setItem(stateKey, JSON.stringify(state));
      }
    } catch (e) {
      console.error('Error updating local storage column order', e);
    }
  }
}